import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react';

import { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';

import { useLocalStorage } from 'react-use';
import { updateUser } from '../api/approval';
import { fetchItemsWithFilter, Filter } from '../api/generic';
import { auth, db, registerServiceWorkerAndGetToken } from '../config/firebase';
import { saveToken } from '../config/tokens';
import { Lang } from '../lang/types';
import Tenant from '../models/Tenant';
import { UserData } from '../models/UserData';
import { IChildrenProps } from '../types';

interface IAuthContextState {
  currentUser: User | null;
  loading: boolean;
  userObject: UserData | null;
  tenant: string;
  department?: string;
  setDepartment: (val: string) => void;
  tier: number;
  tenantObject: Tenant | null;
  changeTenant: (tenant: string) => Promise<void>;
  handleFCMToken: (userId: string) => Promise<void>;
  language: Lang;
  setLanguage: (val: Lang) => void;

  currency: (amount: number | string) => string;
}

const AuthContext = createContext({} as IAuthContextState);

const initUserObject: UserData = {
  name: '',
  email: '',
  role: 'user',
  approved: false,
  denied: false,
  uid: '',
  authProvider: '',
  photoURL: '',
  tenant: 'trail',
  favorites: { inventory: [], recipes: [] },
};

export const handleFCMToken = async (userId: string): Promise<void> => {
  try {
    const fcmToken = await registerServiceWorkerAndGetToken();
    if (fcmToken) {
      await saveToken(userId, fcmToken);
      // console.log(`FCM token saved for user ${userId}: ${fcmToken}`);
    } else {
      // console.warn("No FCM token retrieved. Check notification permissions.");
    }
  } catch (error) {
    console.error(`Error handling FCM token for user ${userId}:`, error);
  }
};

export const AuthContextProvider: FC<IChildrenProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userObject, setUserObject] = useState<UserData | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  const [department, setDepartment] = useLocalStorage('departmentFilter', '');
  const [language, setLang] = useLocalStorage<Lang>('language', 'en');

  const fetchTenant = async (tenantId: string): Promise<void> => {
    const filter: Filter<Tenant> = {
      field: 'name',
      operator: '==',
      value: tenantId,
    };
    const t = await fetchItemsWithFilter<Tenant>('tenant', [filter]);
    setTenant(t[0]);
  };

  const changeTenant = async (t: string) => {
    if (userObject?.role === 'god') await updateUser(userObject.uid, { tenant: t });
  };

  const currency = (amount: number | string): string => {
    const curr = tenant?.currency ?? '€';
    const position = tenant?.currencyPosition ?? 'before';

    return position === 'before' ? `${curr} ${amount}` : `${amount} ${curr}`;
  };

  useEffect(() => {
    if (userObject?.tenant) fetchTenant(userObject.tenant);
  }, [userObject?.tenant]);

  useEffect(() => {
    // add currentUser changes to userObject
    if (currentUser && userObject && !userObject.denied) {
      const newUserObject = { ...userObject };
      newUserObject.name = currentUser.displayName ?? '';
      newUserObject.email = currentUser.email ?? '';
      newUserObject.photoURL = currentUser.photoURL ?? '';
      setUserObject(newUserObject);

      // update userObject in Firestore
      updateUser(newUserObject.uid, newUserObject);
    }
  }, [currentUser]);

  useEffect(() => {
    let unsubscribeFromUserObject = () => {};

    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false);

      if (user) {
        const userRef = doc(db, 'users', user.uid);

        // Set up a real-time listener for the user's document
        unsubscribeFromUserObject = onSnapshot(
          userRef,
          async (doc) => {
            if (doc.exists()) {
              setUserObject(doc.data() as UserData);
            } else {
              // Handle case where user does not have a Firestore document
              setUserObject({
                ...initUserObject,
                name: user.displayName ?? '',
                email: user.email ?? '',
                authProvider: user.providerId,
                photoURL: user.photoURL ?? '',
                tenant: 'trail',
              });
            }

            // Handle FCM token
            await handleFCMToken(user.uid);
          },
          (error) => {
            console.error('Error fetching user data:', error);
            // Handle error
          },
        );
      } else {
        setUserObject(null); // Clear user object when not logged in
      }
    });

    return () => {
      unsubscribe();
      unsubscribeFromUserObject();
    };
  }, []);

  const values = useMemo(
    () => ({
      currentUser,
      loading,
      userObject,
      tenant: userObject?.tenant ?? 'trail',
      tenantObject: tenant,
      tier: tenant?.tier ?? 1,
      department: tenant?.departments?.find((d) => d.name === department)?.name ?? '',
      setDepartment,
      language: language ?? 'en',
      setLanguage: setLang as (val: Lang) => void,
      handleFCMToken,
      changeTenant,
      currency,
    }),
    [currentUser, loading, userObject, tenant, department, language],
  );

  return <AuthContext.Provider value={values}>{!loading && children}</AuthContext.Provider>;
};

export const useAuthContext = (): IAuthContextState => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('`useAuthContext` hook must be used within a `AuthContextProvider` component');
  }
  return context;
};
